import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiPlus, FiEdit2, FiTrash2, FiServer, FiSave } from "react-icons/fi";

import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";

import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
// Shared price display: follows the system USD/TL toggle in the header
import ProductPrice from "../../../components/CustomUI/ProductPrice";
import Pagination from "../../../components/BaseUI/Pagination";
import { useTitle } from "../../../hooks/useTitle";
const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const DeviceModels = () => {
  const [models, setModels] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sort, setSort] = useState({ sortBy: "code", sortOrder: "asc" });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [selectedModel, setSelectedModel] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editFormData, setEditFormData] = useState({
    code: "",
    name: "",
    description: "",
    status: "active",
    price: 0,
  });
  useTitle("Rovion MES - Ürün Modelleri");
  const format = useFormatValue;
  const navigate = useNavigate();
  const { getToken, user } = useAuth();

  // Page-level write permission for device models
  const canEdit =
    !!user &&
    hasPageWriteAccess(
      user.role,
      "/device-models",
      user.pages || [],
      !!user.fullAccess,
    );

  const headers = [
    {
      name: translate("UI.MODEL_CODE"),
      dataType: "text",
      id: 0,
      sortKey: "code",
    },
    {
      name: translate("UI.MODEL_NAME"),
      dataType: "text",
      id: 1,
      sortKey: "name",
    },
    {
      name: translate("UI.PRICE"),
      dataType: "number",
      id: 2,
      sortKey: "price",
      customSorter: (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0),
    },
    {
      name: translate("UI.STATUS"),
      dataType: "text",
      id: 3,
      sortKey: "status",
      customSorter: (a, b) =>
        String(a.status || "").localeCompare(String(b.status || "")),
    },
    {
      name: translate("UI.CREATED"),
      dataType: "date",
      id: 4,
      sortKey: "createdAt",
      customSorter: (a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    { name: translate("UI.ACTIONS"), id: 5, sortable: false },
  ];

  const {
    visibleColumns,
    toggleColumnVisibility,
    visibleColumnsList,
    resetToDefault,
  } = useColumnVisibility("device-models-list", headers);

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.MODELS") },
  ];

  useEffect(() => {
    fetchModels();
  }, [
    pagination.page,
    pagination.limit,
    searchQuery,
    sort.sortBy,
    sort.sortOrder,
  ]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/device-models`, {
        params: {
          paginate: true,
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery || undefined,
          sortBy: sort.sortBy,
          sortOrder: sort.sortOrder,
        },
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setModels(response.data.models || []);
      if (response.data.pagination) {
        setPagination((prev) => ({ ...prev, ...response.data.pagination }));
      }
    } catch (error) {
      console.error("Model getirme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message || translate("UI.MODEL_FETCH_ERROR"),
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (model) => {
    setSelectedModel(model);
    setEditFormData({
      code: model.code,
      name: model.name,
      description: model.description || "",
      status: model.status,
      price: model.price || 0,
    });

    // Fiyat aralıklarını set et

    setSidebarOpened(true);
  };

  const handleDelete = (model) => {
    setSelectedModel(model);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(
        `${API_URL}/api/device-models/${selectedModel.code}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description: translate("UI.MODEL_DELETE_SUCCESS"),
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);
        setShowDeleteModal(false);
        setSelectedModel(null);
        fetchModels();
      } else {
        setToasts([
          {
            description:
              response.data?.message || translate("UI.MODEL_DELETE_ERROR"),
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
      }
    } catch (error) {
      console.error("Model silme hatası:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        translate("UI.MODEL_DELETE_ERROR");

      setToasts([
        {
          description: errorMessage,
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (value, name) => {
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStatusChange = (status) => {
    setEditFormData((prev) => ({
      ...prev,
      status,
    }));
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();

    if (!editFormData.code || !editFormData.name) {
      setToasts([
        {
          description: translate("UI.FILL_REQUIRED_FIELDS"),
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.put(
        `${API_URL}/api/device-models/${selectedModel.code}`,
        editFormData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description: translate("UI.MODEL_UPDATE_SUCCESS"),
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);
        fetchModels();
        setSidebarOpened(false);
        setEditFormData({
          code: "",
          name: "",
          description: "",
          status: "active",
          price: 0,
        });
      }
    } catch (error) {
      console.error("Model güncelleme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message || translate("UI.MODEL_UPDATE_ERROR"),
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const getStatusAppearance = (status) => {
    switch (status) {
      case "active":
        return "success-subtle";
      case "inactive":
        return "danger-subtle";
      default:
        return "neutral-subtle";
    }
  };

  const closeSidebar = () => {
    setSidebarOpened(false);
    setSelectedModel(null);
    setEditFormData({
      code: "",
      name: "",
      description: "",
      price: 0,
      status: "active",
    });
  };

  return (
    <AppPage
      // title={translate("UI.MODELS")}
      breadcrumbs={breadcrumbs}
    >
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="page-header-card p-3 d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-0">{translate("UI.MODELS")}</h2>
            </div>
            {canEdit && (
              <div className="d-flex gap-2">
                <Button
                  appearance="primary-animated"
                  onClick={() => navigate("/device-models/create")}
                  icon={<FiPlus />}
                >
                  {translate("UI.ADD_DEVICE_MODEL")}
                </Button>
              </div>
            )}
          </div>
          <div className="card shadow-sm">
            <div className="card-body">
              <Table
                columns={headers}
                visibleColumns={visibleColumns}
                tableId="device-models-list"
                checkboxSelection
                loading={loading}
                externalSearch={true}
                viewOptions={
                  <ListViewOptionsPanel
                    columns={headers}
                    visibleColumns={visibleColumns}
                    onToggleColumn={toggleColumnVisibility}
                    onResetColumns={resetToDefault}
                    showApply={false}
                    showReset={false}
                  />
                }
                onSearch={(value) => {
                  setSearchQuery(value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                searchPlaceholder="Model adı veya kodu ile ara..."
                externalSorting={true}
                onSort={(nextSort) => {
                  setSort(nextSort);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                hideFooter={true}
              >
                {models.map((model, index) => (
                  <Tr key={index} id={model?.code || index} data={model}>
                    {visibleColumns.includes(0) && <Td>{model.code}</Td>}
                    {visibleColumns.includes(1) && <Td>{model.name}</Td>}
                    {visibleColumns.includes(2) && (
                      <Td>
                        <ProductPrice value={model.price} />
                      </Td>
                    )}
                    {visibleColumns.includes(3) && (
                      <Td>
                        <Lozenge appearance={getStatusAppearance(model.status)}>
                          {translate(`UI.${model.status?.toUpperCase()}`)}
                        </Lozenge>
                      </Td>
                    )}
                    {visibleColumns.includes(4) && (
                      <Td>{format(model.createdAt, "date")}</Td>
                    )}
                    {visibleColumns.includes(5) && (
                      <Td>
                        {canEdit && (
                          <div className="d-flex align-items-center gap-1">
                            <IconButton
                              icon={<FiEdit2 style={{ fontSize: "18px" }} />}
                              appearance="subtle"
                              shape="circle"
                              onClick={() => handleEdit(model)}
                            />
                            <IconButton
                              icon={<FiTrash2 style={{ fontSize: "18px" }} />}
                              appearance="subtle"
                              shape="circle"
                              onClick={() => handleDelete(model)}
                            />
                          </div>
                        )}
                      </Td>
                    )}
                  </Tr>
                ))}
              </Table>
              {!loading && pagination.total > 0 && (
                <div className="mt-3">
                  <Pagination
                    totalItems={pagination.total}
                    totalPerPage={pagination.limit}
                    currentPage={pagination.page}
                    totalRecords={pagination.total}
                    onPageChange={({ pagination: paginationData }) => {
                      setPagination((prev) => ({
                        ...prev,
                        page:
                          prev.limit !== paginationData.itemsPerPage
                            ? 1
                            : paginationData.currentPage,
                        limit: paginationData.itemsPerPage,
                      }));
                    }}
                    options={{
                      skipPrevious: true,
                      previous: true,
                      next: true,
                      skipNext: true,
                      selectQuantity: true,
                      simpleCounting: false,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </ContainerItem>
      </Container>

      {/* Silme Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <ModalHeader>{translate("UI.DELETE_CONFIRMATION")}</ModalHeader>
        <ModalBody>{translate("UI.MODEL_DELETE_CONFIRM")}</ModalBody>
        <ModalFooter>
          <Button appearance="subtle" onClick={() => setShowDeleteModal(false)}>
            {translate("UI.CANCEL")}
          </Button>
          <Button appearance="danger" onClick={confirmDelete} loading={loading}>
            {translate("UI.DELETE")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Düzenleme Sidebar — TL toggle only affects this form */}
      <SidebarContainer
        show={sidebarOpened}
        title={translate("UI.EDIT_MODEL")}
        onClose={closeSidebar}
      >
          <div className="d-flex flex-column">
            <div className="device-header">
              <FiServer
                className="text-primary"
                style={{ width: "24px", height: "24px" }}
              />
              <div>
                <h5 className="mb-0">{selectedModel?.code}</h5>
                <small className="text-muted">{selectedModel?.name}</small>
              </div>
            </div>

            <div className="form-container">
              <form onSubmit={handleSubmitEdit}>
                <div className="form-group">
                  <Input
                    label={translate("UI.MODEL_CODE")}
                    name="code"
                    value={editFormData.code}
                    onChange={(value) => handleInputChange(value, "code")}
                    required
                    helpText={translate("UI.MODEL_CODE_DESC")}
                  />
                </div>

                <div className="form-group">
                  <Input
                    label={translate("UI.MODEL_NAME")}
                    name="name"
                    value={editFormData.name}
                    onChange={(value) => handleInputChange(value, "name")}
                    required
                    helpText={translate("UI.MODEL_NAME_DESC")}
                  />
                </div>
                <div className="form-group">
                  <Input
                    label={`${translate("UI.PRICE")} (USD)`}
                    name="price"
                    value={editFormData.price}
                    onChange={(value) => handleInputChange(value, "price")}
                    required
                    helpText={translate("UI.PRICE_DESC")}
                  />
                </div>
                <div className="form-group">
                  <Label>{translate("UI.STATUS")}</Label>
                  <div className="form-control p-0">
                    <Dropdown
                      placement="top-start"
                      trigger={
                        <div className="d-flex align-items-center justify-content-between p-2 w-100">
                          <Lozenge
                            appearance={getStatusAppearance(
                              editFormData.status,
                            )}
                          >
                            {translate(
                              `UI.${editFormData.status.toUpperCase()}`,
                            )}
                          </Lozenge>
                        </div>
                      }
                    >
                      <DropdownContent className="w-100">
                        <DropdownItem
                          onClick={() => handleStatusChange("active")}
                          active={editFormData.status === "active"}
                        >
                          <Lozenge appearance="success-subtle">
                            {translate("UI.ACTIVE")}
                          </Lozenge>
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => handleStatusChange("inactive")}
                          active={editFormData.status === "inactive"}
                        >
                          <Lozenge appearance="danger-subtle">
                            {translate("UI.INACTIVE")}
                          </Lozenge>
                        </DropdownItem>
                      </DropdownContent>
                    </Dropdown>
                  </div>
                </div>

                <div className="form-group">
                  <Input
                    type="textarea"
                    label={translate("UI.DESCRIPTION")}
                    name="description"
                    value={editFormData.description}
                    onChange={(value) =>
                      handleInputChange(value, "description")
                    }
                    rows={4}
                    helpText={translate("UI.MODEL_DESCRIPTION_DESC")}
                  />
                </div>
              </form>
            </div>

            <div className="form-actions">
              <Button
                appearance="primary-animated"
                onClick={handleSubmitEdit}
                loading={loading}
                className="d-flex align-items-center gap-2"
              >
                <FiSave size={16} />
                {translate("UI.SAVE")}
              </Button>
            </div>
          </div>
      </SidebarContainer>

      <style>
        {`
                .device-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 16px;
                    margin-bottom: 24px;
                    background: #f8f9fa;
                    border-radius: 8px;
                }

                .form-container {
                    padding: 0 16px;
                }

                .form-group {
                    margin-bottom: 20px;
                    position: relative;
                }

                .form-group:last-child {
                    margin-bottom: 0;
                }

                .form-actions {
                    margin-top: 24px;
                    padding: 16px;
                    border-top: 1px solid #e9ecef;
                }

                .form-control {
                    display: block;
                    width: 100%;
                    padding: 0;
                    font-size: 0.875rem;
                    font-weight: 400;
                    line-height: 1.5;
                    background-color: #fff;
                    background-clip: padding-box;
                    border: 1px solid #e2e5e8;
                    border-radius: 0.375rem;
                    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                }
                
                .form-control:hover {
                    border-color: var(--bs-primary);
                }

                .dropdown-container {
                    position: static;
                    width: 100%;
                }

                .dropdown-container__content {
                    width: 100% !important;
                    max-height: 300px;
                    overflow-y: auto;
                    position: absolute;
                    z-index: 1000;
                    background: white;
                    border: 1px solid #e2e5e8;
                    border-radius: 0.375rem;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    margin: 0;
                    padding: 0.5rem 0;
                    left: 0;
                }

                .dropdown-container__content.top-start {
                    bottom: 100%;
                    margin-bottom: 0.5rem;
                }

                .dropdown__item {
                    padding: 8px 16px;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: background-color 0.2s;
                }

                .dropdown__item:hover {
                    background-color: var(--bs-primary-bg-subtle);
                }

                .content__list {
                    margin: 0;
                    padding: 0;
                    list-style: none;
                }
                
                .column-selector {
                    min-width: 220px;
                }
                
                .column-selector .dropdown__item {
                    padding: 8px 12px;
                }
                
                .column-selector input[type="checkbox"] {
                    margin: 0;
                }
                `}
      </style>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            // actions={[{ content: "Kapat", onClick: () => removeToast(index) }]}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(DeviceModels);
